// src/components/common/Gauge/Gauge.jsx
import React, { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import styles from './Gauge.module.css';

ChartJS.register(ArcElement, Tooltip);

const readCssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

/**
 * Locally-scoped plugin (not globally registered) that draws the achieved value + "% of target"
 * text in the center of the half-doughnut ring.
 */
const centerTextPlugin = {
  id: 'gaugeCenterText',
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const gaugeText = chart.config.options.gaugeText;
    if (!gaugeText || !chartArea) return;

    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = chartArea.bottom;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.fillStyle = readCssVar('--color-text-primary', '#1f2937');
    ctx.font = '700 22px sans-serif';
    ctx.fillText(gaugeText.achievedLabel, cx, cy - 34);

    ctx.fillStyle = readCssVar('--color-text-secondary', '#6b7280');
    ctx.font = '600 13px sans-serif';
    if (gaugeText.percentLabel) {
      ctx.fillText(gaugeText.percentLabel, cx, cy - 13);
    }
    if (gaugeText.subLabel) {
      ctx.fillText(gaugeText.subLabel, cx, cy + 10);
    }
    ctx.restore();
  },
};

/**
 * Half-doughnut "Achieved vs Target" gauge.
 *
 * @param {Object} props
 * @param {number} props.value - Achieved value
 * @param {number} props.target - Target value (0 = no target set, renders a full ring)
 * @param {string} [props.label] - Small caption drawn under the numbers, e.g. "Today"
 * @param {function} [props.formatValue] - Formats value/target for display (default: toLocaleString)
 * @param {number} [props.size] - Rendered width in px (default 220)
 * @param {string} [props.achievedColor] - Override achieved-arc color (default: CSS var --color-success)
 * @param {string} [props.remainingColor] - Override remaining-arc color (default: CSS var --color-gray-200)
 * @param {number} [props.animationDurationMs] - Chart.js arc animation duration (default 900)
 */
const Gauge = ({
  value = 0,
  target = 0,
  label,
  formatValue = (v) => v.toLocaleString(),
  size = 260,
  achievedColor,
  remainingColor,
  animationDurationMs = 900,
}) => {
  const achieved = Math.max(0, value);
  const hasTarget = target > 0;
  const isOver = hasTarget && achieved > target;
  const filled = hasTarget ? Math.min(achieved, target) : achieved;
  const remaining = hasTarget ? Math.max(target - achieved, 0) : 0;
  const percent = hasTarget ? Math.round((achieved / target) * 100) : 0;

  const achievedColorResolved = achievedColor || readCssVar('--color-success', '#10b981');
  const overColorResolved = readCssVar('--color-info', '#3b82f6');
  const remainingColorResolved = remainingColor || readCssVar('--color-gray-200', '#e5e7eb');

  const data = useMemo(() => ({
    datasets: [{
      data: [filled, remaining],
      backgroundColor: [isOver ? overColorResolved : achievedColorResolved, remainingColorResolved],
      borderWidth: 0,
    }],
  }), [filled, remaining, isOver, achievedColorResolved, overColorResolved, remainingColorResolved]);

  const options = {
    rotation: -90,
    circumference: 180,
    cutout: '58%',
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: animationDurationMs, easing: 'easeOutQuart' },
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    gaugeText: {
      achievedLabel: formatValue(achieved),
      percentLabel: hasTarget ? `${percent}% of ${formatValue(target)}` : '',
      subLabel: label,
    },
  };

  return (
    <div className={styles.gaugeWrapper} style={{ width: size, height: size / 2 + 55 }}>
      <Doughnut data={data} options={options} plugins={[centerTextPlugin]} />
    </div>
  );
};

export default Gauge;
